import { storyblokEditable } from "gatsby-source-storyblok"
import { AnimatePresence } from "motion/react"
import type { Transition } from "motion/react"
import * as m from "motion/react-m"

import IconCTAPopup from "./IconCTAPopup"
import CloseIcon from "../../elements/V2/Icons/CloseIcon"

import { Color } from "@constants/V2/color"
import { sessionStorageAvailable } from "@utils/shop"
import { storyblokLinkToYocoLink } from "@utils/storyblok"
import { getAccentColor } from "@utils/V2/color"
import React, { useEffect, useState } from "react"

type Props = {
  blok: Storyblok.IconCTAPopup
  id: string
  timeToShow?: number
  alwaysShow?: boolean
}

const POPUP_TRANSITION: Transition = { duration: 0.3, ease: "easeInOut" }

const SiteWidePopup = ({ blok, id, timeToShow, alwaysShow = false }: Props) => {
  const [isOpen, setIsOpen] = useState(false)

  const dismissalKey = `sitewide-popup-dismissed-${id}`

  useEffect(() => {
    if (alwaysShow) {
      setIsOpen(true)
      return
    }

    if (sessionStorageAvailable() && sessionStorage.getItem(dismissalKey)) {
      return
    }

    const timer = setTimeout(
      () => {
        setIsOpen(true)

        if (sessionStorageAvailable()) {
          sessionStorage.setItem(dismissalKey, "true")
        }
      },
      Math.max(0, timeToShow ?? 0) * 1000
    )

    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const ctaLink = storyblokLinkToYocoLink(blok.ctaLink)

  return (
    <AnimatePresence>
      {isOpen && (
        <m.div
          className="fixed inset-x-0 bottom-0 z-50 flex justify-center px-16 pb-16"
          initial={{ y: "100%" }}
          animate={{ y: 0 }}
          exit={{ y: "100%" }}
          transition={POPUP_TRANSITION}
        >
          <div
            // @ts-expect-error to have more strict typing compared to Storybloks generic object typing
            {...storyblokEditable(blok)}
            className="relative w-full max-w-[840px]"
          >
            <IconCTAPopup
              text={blok.text}
              ctaText={blok.ctaText}
              ctaLink={ctaLink}
              icon={blok.icon?.filename}
              iconAlt={blok.icon?.alt}
              backgroundColor={blok.backgroundColor}
              primaryBorderColor={blok.primaryBorderColor}
              secondaryBorderColor={blok.secondaryBorderColor}
            />
            <button
              type="button"
              aria-label="Close popup"
              onClick={() => setIsOpen(false)}
              className="absolute right-12 top-12 h-24 w-24 cursor-pointer opacity-50 transition-opacity hover:opacity-100"
            >
              <CloseIcon
                width="24px"
                height="24px"
                strokeWidth="2"
                strokeColor={getAccentColor(
                  blok.backgroundColor ?? Color.Black
                )}
              />
            </button>
          </div>
        </m.div>
      )}
    </AnimatePresence>
  )
}

export default SiteWidePopup
